import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosDelete, axiosGet, axiosPost } from '../api/axios';
import { toast } from 'react-toastify';

export interface ClientAdministrator {
  access_role: string;
  administrator_role: AdministratorRole;
  administrator_role_id: number;
  api_key: string;
  caller_role_id: number;
  city: string;
  client_administrator_role: ClientAdministratorRole;
  client_administrator_role_id: number;
  counseling_configuration: JSON;
  created_at: string;
  current_app_version: string;
  date_of_birth: string;
  dialcare_processing_date: string;
  display_name: string;
  display_name_rejected: boolean;
  eligibility_email: string;
  email_address: string;
  family: string;
  first_name: string;
  gender: string;
  how_did_you_hear: string;
  id: number;
  is_dependent: boolean;
  is_mms_compatible_phone: boolean;
  is_partial: boolean;
  is_text_compatible_phone: boolean;
  is_text_unsubscribed: boolean;
  keycloak_user_id: string;
  languages: string[];
  last_active_at: string;
  last_name: string;
  listener_role_id: number;
  mobile_phone: string;
  mobile_phone_verification_started_at: string;
  mobile_phone_verification_token: string;
  mobile_phone_verified: boolean;
  needs_password: boolean;
  password_reset_token: string;
  password_reset_token_created_at: string;
  pronoun: string;
  proposed_display_name: string;
  race: string;
  relationship: string;
  relationship_type: string;
  search_queries: any[];
  spirituality: string;
  state: string;
  status: string;
  street_address_1: string;
  street_address_2: string;
  test_user: boolean;
  timezone: string;
  updated_at: string;
  user_subscription: any;
  zip_code: string;
}

export interface AdministratorRole {
  type: string;
}

export interface ClientAdministratorRole {
  created_at: string;
  id: number;
  status: string;
  type: string;
}

const getClientAdministrators = async (clientId: string): Promise<ClientAdministrator[]> => {
  return await axiosGet(`/clients/${clientId}/client_administrators`, {}, 'v2').then(
    (clientAdministrators) => clientAdministrators.data,
  );
};

export const useClientAdministrators = (clientId: string) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<ClientAdministrator[]>(
    ['clientAdministrators', clientId],
    () => getClientAdministrators(clientId),
  );

  return { data, isLoading, error, refetch, isFetching };
};

const inviteClientAdministratorRequest = async (
  clientId: string,
  emailAddress: string,
): Promise<ClientAdministrator> => {
  return await axiosPost(
    `/clients/${clientId}/invite_client_administrator`,
    { email_address: emailAddress },
    'v2',
  ).then((clientAdministrator) => clientAdministrator.data);
};

export const useInviteClientAdministrator = (clientId: string) => {
  const queryClient = useQueryClient();
  const inviteClientAdministrator = useMutation(
    (emailAddress: string) => inviteClientAdministratorRequest(clientId, emailAddress),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clientAdministrators', clientId]);
        toast.success('Email Sent Successfully');
      },
      onError: (error) => {
        console.error(error);
      },
    },
  );

  return { inviteClientAdministrator };
};

const resendClientAdministratorInviteRequest = async (clientId: string, id: string): Promise<ClientAdministrator> => {
  return await axiosPost(`/clients/${clientId}/client_administrator/${id}/resend_invite`, {}, 'v2').then(
    (clientAdministrator) => clientAdministrator.data,
  );
};

export const useResendClientAdministratorInvite = (clientId: string) => {
  const queryClient = useQueryClient();
  const resendClientAdministratorInvite = useMutation(
    (id: string) => resendClientAdministratorInviteRequest(clientId, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clientAdministrators', clientId]);
        toast.success('Administrator invite resent successfully');
      },
      onError: (error) => {
        console.error(error);
      },
    },
  );

  return { resendClientAdministratorInvite };
};

const deleteClientAdministratorRequest = async (
  clientId: string,
  id: string,
  emailAddress: string,
): Promise<ClientAdministrator> => {
  return await axiosDelete(
    `/clients/${clientId}/client_administrator/${id}`,
    {
      email_address: emailAddress,
      client_id: clientId,
    },
    'v2',
  ).then((clientAdministrator) => clientAdministrator.data);
};

export const useDeleteClientAdministrator = (clientId: string) => {
  const queryClient = useQueryClient();
  const deleteClientAdministrator = useMutation(
    ({ id, emailAddress }: { id: string; emailAddress: string }) =>
      deleteClientAdministratorRequest(clientId, id, emailAddress),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clientAdministrators', clientId]);
        toast.success('Administrator deleted successfully');
      },
      onError: (error) => {
        console.error(error);
      },
    },
  );

  return { deleteClientAdministrator };
};

const revokeClientAdministratorInviteRequest = async (
  clientId: string,
  userId: string,
  emailAddress: string,
): Promise<ClientAdministrator> => {
  return await axiosDelete(
    `/users/${userId}/client_administrator_role`,
    {
      email_address: emailAddress,
      client_id: clientId,
    },
    'v2',
  ).then((clientAdministrator) => clientAdministrator.data);
};

export const useRevokeClientAdministratorInvite = (clientId: string) => {
  const queryClient = useQueryClient();
  const revokeClientAdministratorInvite = useMutation(
    ({ userId, emailAddress }: { userId: string; emailAddress: string }) =>
      revokeClientAdministratorInviteRequest(clientId, userId, emailAddress),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clientAdministrators', clientId]);
        toast.success('Administrator invite revoked successfully');
      },
      onError: (error) => {
        console.error(error);
      },
    },
  );

  return { revokeClientAdministratorInvite };
};
